import { useRequiredContext } from "@redotech/react-util/context";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getShopifyCollections } from "@redotech/redo-merchant-app-common/client/shopify";
import { StorefrontClientContext } from "@redotech/redo-merchant-app-common/team";
import {
  PostPurchaseSegmentStep,
  postPurchaseSegmentStepSchema,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { defaultPostPurchaseSegmentConditionBlock } from "@redotech/redo-model/post-purchase/node-defaults";
import { postPurchaseSegmentInProgressSchema } from "@redotech/redo-model/post-purchase/segment-in-progress";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  PostPurchaseSegmentConditionBlock,
  PostPurchaseSegmentConditionBlockProps,
} from "@redotech/redo-web/conversion/post-purchase/segment-builder-editor";
import { Flex } from "@redotech/redo-web/flex";
import { ConditionConnector } from "@redotech/redo-web/marketing/segments/condition-connector";
import { SegmentConjunctionSelect } from "@redotech/redo-web/marketing/segments/segment-conjunction-select";
import { storefrontProductToModalProduct } from "@redotech/redo-web/product/product-selection-modal";
import {
  getCollectionsByGids as _getCollectionsByGids,
  getProductsByGids as _getProductsByGids,
  searchProducts,
} from "@redotech/shopify-storefront";
import {
  convertShopifyIdToGid,
  Gid,
  ShopifyIdType,
} from "@redotech/shopify-util/id";
import { interleave, remove, replace } from "@redotech/util/array";
import { memo } from "react";
import {
  FrontendStepType,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "../../steps";

export const postPurchaseSegmentStepType: FrontendStepType<
  PartialWithRequiredType<PostPurchaseSegmentStep>
> = {
  type: "standard",
  valid: (step) => postPurchaseSegmentStepSchema.safeParse(step).success,
  selectable: (_) => true,
  inspectorFactory: (props) => ({
    header: { title: "Post-purchase segment" },
    sections: [
      {
        fields: [
          {
            widget: "custom",
            Element: ({ value: step }) => (
              <PostPurchaseSegmentInspector
                {...props}
                readonly={props.readonly}
                setStep={props.setStep}
                step={step}
              />
            ),
          },
        ],
      },
    ],
  }),
};

const PostPurchaseSegmentInspector = memo(
  function PostPurchaseSegmentInspector({
    step,
    setStep,
    readonly,
  }: StepTypeDetailsProps<PartialWithRequiredType<PostPurchaseSegmentStep>> & {
    readonly?: boolean;
  }) {
    const storefrontClient = useRequiredContext(StorefrontClientContext);
    const merchantClient = useRequiredContext(RedoMerchantClientContext);
    const segment = postPurchaseSegmentInProgressSchema.parse(step.segment);

    const conditionBlockElements = segment.conditionBlocks.map(
      (block, index) => {
        const getProductsByGids = async (ids: Gid[]) => {
          return await _getProductsByGids(storefrontClient, ids);
        };
        const getCollectionsByGids = async (ids: Gid[]) => {
          return await _getCollectionsByGids(storefrontClient, ids);
        };
        const blockProps: PostPurchaseSegmentConditionBlockProps = readonly
          ? { block, readonly, getProductsByGids, getCollectionsByGids }
          : {
              block,
              readonly,
              getProductsByGids,
              getCollectionsByGids,
              searchProducts: async ({ search }) => {
                const results = await searchProducts(
                  storefrontClient,
                  search,
                  100,
                );
                return results.map(storefrontProductToModalProduct);
              },
              searchCollections: async ({ search }) => {
                const collections = await getShopifyCollections(
                  merchantClient,
                  { searchString: search },
                );
                return collections.map((collection) => ({
                  id: convertShopifyIdToGid(
                    collection.id,
                    ShopifyIdType.Collection,
                  ),
                  title: collection.name,
                }));
              },
              setBlock: (updatedBlock) => {
                setStep({
                  ...step,
                  segment: {
                    ...segment,
                    conditionBlocks: replace(
                      segment.conditionBlocks,
                      index,
                      updatedBlock,
                    ),
                  },
                });
              },
              deleteBlockRequested: () => {
                setStep({
                  ...step,
                  segment: {
                    ...segment,
                    conditionBlocks: remove(segment.conditionBlocks, index),
                  },
                });
              },
            };

        return (
          <PostPurchaseSegmentConditionBlock {...blockProps} key={index} />
        );
      },
    );

    const joinedConditionBlocks = interleave(
      conditionBlockElements,
      (_, idx) => (
        <ConditionConnector
          conjunction={segment.conjunction}
          key={`connector-${idx}`}
        />
      ),
    );

    return (
      <Flex dir="column" gap="xl">
        <SegmentConjunctionSelect
          mode={segment.conjunction}
          readonly={readonly}
          setMode={(mode) => {
            setStep({ ...step, segment: { ...segment, conjunction: mode } });
          }}
          textAfterConjunction="of these conditions must match the order"
        />

        <Flex dir="column" gap="none">
          {joinedConditionBlocks}
        </Flex>

        {!readonly && (
          <Flex>
            <RedoButton
              hierarchy="secondary"
              IconLeading={PlusIcon}
              onClick={() => {
                setStep({
                  ...step,
                  segment: {
                    ...segment,
                    conditionBlocks: [
                      ...segment.conditionBlocks,
                      defaultPostPurchaseSegmentConditionBlock,
                    ],
                  },
                });
              }}
              text="Add condition group"
            />
          </Flex>
        )}
      </Flex>
    );
  },
);
